import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Header from '../components/Header';
import SolarSystemScene2D from '../components/SolarSystemScene2D';
import LegendaryReveal from '../components/LegendaryReveal';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { allElements } from '../data/elements';

function SystemView() {
  const { coords } = useParams();
  const navigate = useNavigate();
  const { api, user, updateCredits } = useAuth();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [persisting, setPersisting] = useState(false);
  const [selectedPlanet, setSelectedPlanet] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  
  // New states for naming and moon scanning
  const [planetName, setPlanetName] = useState('');
  const [naming, setNaming] = useState(false);
  const [selectedMoon, setSelectedMoon] = useState(null);
  const [moonScanResult, setMoonScanResult] = useState(null);
  const [scanningMoon, setScanningMoon] = useState(false);
  const [moonName, setMoonName] = useState('');
  const [namingMoon, setNamingMoon] = useState(false);
  // Phase 5 — home base
  const [hasBase, setHasBase] = useState(false);
  const [claiming, setClaiming] = useState(false);
  // Phase A — controlling faction of this system
  const [factionInfo, setFactionInfo] = useState(null);

  useEffect(() => {
    persistAndLoadSystem();
  }, [coords]);

  useEffect(() => {
    let active = true;
    api.get(`/api/factions/system?coords_key=${encodeURIComponent(coords)}`)
      .then(({ data }) => { if (active) setFactionInfo(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [coords]);

  useEffect(() => {
    let active = true;
    api.get('/api/base').then(({ data }) => { if (active) setHasBase(!!data.has_base); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const claimBase = async (moonIdx = null) => {
    setClaiming(true);
    try {
      const payload = { coords_key: coords, planet_idx: selectedPlanet.idx };
      if (moonIdx !== null) payload.moon_idx = moonIdx;
      const { data } = await api.post('/api/base/claim', payload);
      if (data?.credits != null) updateCredits?.(data.credits);
      setHasBase(true);
      toast.success(`Home base established on ${data.base.body_name}!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim base');
    } finally {
      setClaiming(false);
    }
  };

  const persistAndLoadSystem = async () => {
    setPersisting(true);
    try {
      const response = await api.post('/api/systems/persist', { coords });
      setSystem(response.data);
    } catch (error) {
      console.error('Failed to persist system:', error);
    } finally {
      setPersisting(false);
      setLoading(false);
    }
  };

  const handlePlanetClick = (planet) => {
    setSelectedPlanet(planet);
    setScanResult(null);
    setPlanetName(planet.custom_name || '');
    setSelectedMoon(null);
    setMoonScanResult(null);
    setMoonName('');
    
    // Check if already scanned by current user
    if (planet.scans && planet.scans.length > 0) {
      const userScan = planet.scans.find(s => s.user_email === user.email);
      if (userScan) {
        setScanResult(userScan.scan_data);
      }
    }
  };

  const handleScanPlanet = async () => {
    if (!selectedPlanet || user.credits < 25) return;

    setScanning(true);
    try {
      const response = await api.post('/api/planets/scan', {
        coords_key: coords,
        planet_idx: selectedPlanet.idx
      });
      setScanResult(response.data.scan_data);
      updateCredits(response.data.credits);

      // Update system with new scan
      const updatedPlanets = [...system.planets];
      if (!updatedPlanets[selectedPlanet.idx].scans) {
        updatedPlanets[selectedPlanet.idx].scans = [];
      }
      updatedPlanets[selectedPlanet.idx].scans.push({
        user_email: user.email,
        scan_data: response.data.scan_data
      });
      setSystem({ ...system, planets: updatedPlanets });
    } catch (error) {
      console.error('Scan failed:', error);
      alert(error.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleNamePlanet = async () => {
    if (!selectedPlanet || !planetName.trim()) return;

    setNaming(true);
    try {
      const response = await api.post('/api/planets/name', {
        coords_key: coords,
        planet_idx: selectedPlanet.idx,
        name: planetName.trim()
      });
      
      // Update system with new name
      const updatedPlanets = [...system.planets];
      updatedPlanets[selectedPlanet.idx].custom_name = response.data.custom_name;
      updatedPlanets[selectedPlanet.idx].named_by = response.data.named_by;
      updatedPlanets[selectedPlanet.idx].named_by_me = true;
      setSystem({ ...system, planets: updatedPlanets });
      setSelectedPlanet({ ...selectedPlanet, custom_name: response.data.custom_name, named_by: response.data.named_by, named_by_me: true });
      
      alert(response.data.message);
    } catch (error) {
      console.error('Naming failed:', error);
      alert(error.response?.data?.detail || 'Failed to name planet');
    } finally {
      setNaming(false);
    }
  };

  const handleMoonClick = (moon, moonIdx) => {
    setSelectedMoon({ ...moon, moon_idx: moonIdx });
    setMoonScanResult(null);
    setMoonName(moon.custom_name || '');
    
    // Check if already scanned by current user
    if (moon.scans && moon.scans.length > 0) {
      const userScan = moon.scans.find(s => s.user_email === user.email);
      if (userScan) {
        setMoonScanResult(userScan.scan_data);
      }
    }
  };

  const handleScanMoon = async () => {
    if (!selectedPlanet || !selectedMoon || user.credits < 25) return;

    setScanningMoon(true);
    try {
      const response = await api.post('/api/moons/scan', {
        coords_key: coords,
        planet_idx: selectedPlanet.idx,
        moon_idx: selectedMoon.moon_idx
      });
      setMoonScanResult(response.data.scan_data);
      updateCredits(response.data.credits);

      // Update system with new scan
      const updatedPlanets = [...system.planets];
      if (!updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].scans) {
        updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].scans = [];
      }
      updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].scans.push({
        user_email: user.email,
        scan_data: response.data.scan_data
      });
      setSystem({ ...system, planets: updatedPlanets });
      setSelectedMoon({ ...selectedMoon, scans: updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].scans });
    } catch (error) {
      console.error('Moon scan failed:', error);
      alert(error.response?.data?.detail || 'Moon scan failed');
    } finally {
      setScanningMoon(false);
    }
  };

  const handleNameMoon = async () => {
    if (!selectedPlanet || !selectedMoon || !moonName.trim()) return;

    setNamingMoon(true);
    try {
      const response = await api.post('/api/moons/name', {
        coords_key: coords,
        planet_idx: selectedPlanet.idx,
        moon_idx: selectedMoon.moon_idx,
        name: moonName.trim()
      });
      
      // Update system with new name
      const updatedPlanets = [...system.planets];
      updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].custom_name = response.data.custom_name;
      updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].named_by = response.data.named_by;
      updatedPlanets[selectedPlanet.idx].moons[selectedMoon.moon_idx].named_by_me = true;
      setSystem({ ...system, planets: updatedPlanets });
      setSelectedMoon({ ...selectedMoon, custom_name: response.data.custom_name, named_by: response.data.named_by, named_by_me: true });
      
      alert(response.data.message);
    } catch (error) {
      console.error('Moon naming failed:', error);
      alert(error.response?.data?.detail || 'Failed to name moon');
    } finally {
      setNamingMoon(false);
    }
  };

  const handleElementClick = (elementName) => {
    // Try to find element by exact name match first
    let element = allElements.find(el => el.name.toLowerCase() === elementName.toLowerCase());
    
    // If not found, try partial match (e.g., "Water Ice" -> "Water")
    if (!element) {
      element = allElements.find(el => elementName.toLowerCase().includes(el.name.toLowerCase()));
    }
    
    // If still not found, try symbol match
    if (!element) {
      element = allElements.find(el => el.symbol.toLowerCase() === elementName.toLowerCase());
    }
    
    if (element) {
      setSelectedElement(element);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'nonmetal': '#3AD0A1', 'noble-gas': '#7DD3FC', 'alkali-metal': '#FFB86A',
      'alkaline-earth': '#FFE082', 'metalloid': '#A3A3A3', 'halogen': '#FF7A3E',
      'post-transition': '#C4B5FD', 'transition': '#FCA5A5', 'lanthanide': '#F9A8D4',
      'actinide': '#F0ABFC', 'exotic': '#18D2FF', 'cosmic': '#A78BFA',
      'energy': '#FBBF24', 'stellar': '#FCD34D', 'photonic': '#60A5FA',
      'magnetic': '#EC4899', 'dark': '#4B5563', 'crystalline': '#93C5FD',
      'temporal': '#C084FC', 'acoustic': '#34D399', 'radioactive': '#FB923C'
    };
    return colors[category] || '#9CA3AF';
  };

  const getRarityColor = (rarity) => {
    const colors = {
      'Common': '#9CA3AF', 'Uncommon': '#10B981', 'Rare': '#3B82F6',
      'Ultra Rare': '#A855F7', 'Legendary': '#F59E0B', 'Mythic': '#EF4444'
    };
    return colors[rarity] || '#9CA3AF';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!system) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>System not found</p>
      </div>
    );
  }

  const parts = coords.split(':');
  const breadcrumbs = [
    { label: 'ARC Sectors', link: '/explore/octants' },
    { label: `ARC - ${parseInt(parts[0]) + 1}`, link: `/explore/galaxies/${parts[0]}` },
    { label: `Galaxy-${parts[1]}`, link: `/explore/quadrants/${parts[0]}/${parts[1]}` },
    { label: `Quadrant-${parts[2]}`, link: `/explore/sections/${parts[0]}/${parts[1]}/${parts[2]}` },
    { label: `Section-${parts[3]}`, link: `/explore/systems/${parts[0]}/${parts[1]}/${parts[2]}/${parts[3]}` },
    { label: `System-${parts[4]}` }
  ];

  const hasScanResult = selectedPlanet && selectedPlanet.scans && selectedPlanet.scans.find(s => s.user_email === user.email);

  return (
    <div className="min-h-screen pb-20">
      <Header breadcrumbs={breadcrumbs} />

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-[400px,1fr] gap-8">
          {/* Left Panel - Info */}
          <div className="space-y-6">
            {/* System Info */}
            <div className="glass-card rounded-xl p-6">
              <h2 className="text-2xl font-bold mb-4">System-{parts[4]}</h2>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded-full"
                    style={{ backgroundColor: system.star_color, boxShadow: `0 0 15px ${system.star_color}` }}
                  />
                  <span className="text-sm">Star Class: <span className="font-semibold">{system.star_class}</span></span>
                </div>

                <p className="text-sm text-muted-foreground">
                  Coordinates: <code className="text-xs bg-white/10 px-2 py-1 rounded">{coords}</code>
                </p>

                <p className="text-sm text-muted-foreground">
                  Planets: <span className="text-foreground font-semibold">{system.planets.length}</span>
                </p>

                {system.is_colonized && (
                  <div className="mt-3 p-3 bg-green-400/10 rounded-lg border border-green-400/30">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span className="text-sm font-semibold text-green-400">Human Colony</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">This system has been colonized by humanity</p>
                  </div>
                )}

                {factionInfo?.faction && (
                  <div
                    className="mt-3 p-3 rounded-lg border"
                    style={{
                      backgroundColor: `${factionInfo.faction.color}14`,
                      borderColor: `${factionInfo.faction.color}55`,
                    }}
                    data-testid="system-faction-badge"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold" style={{ color: factionInfo.faction.color }}>
                        {factionInfo.faction.name} Space
                      </span>
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border"
                        style={{ color: factionInfo.faction.color, borderColor: `${factionInfo.faction.color}66` }}
                        data-testid="system-faction-standing"
                      >
                        {factionInfo.standing?.tier}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {factionInfo.standing?.refused
                        ? 'You are HOSTILE here — their colonies and stations refuse service.'
                        : factionInfo.standing?.buy_discount > 0
                          ? `Your standing grants ${Math.round(factionInfo.standing.buy_discount * 100)}% shop discount & +${Math.round(factionInfo.standing.sell_bonus * 100)}% sell bonus here.`
                          : 'Complete contracts and trade here to build standing.'}
                    </p>
                  </div>
                )}

                {system.discoverer && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-cyan-400">First Discoverer</p>
                    <p className="text-sm font-semibold">{system.discoverer}</p>
                    {system.discovered_at && (
                      <p className="text-xs text-muted-foreground">{new Date(system.discovered_at).toLocaleString()}</p>
                    )}
                  </div>
                )}

                {system.ai_history && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-xs text-cyan-400 mb-2">System History</p>
                    <p className="text-sm italic leading-relaxed">{system.ai_history}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Planet List */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">Planets</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="planets-list">
                {system.planets.map((planet, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePlanetClick(planet)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedPlanet?.idx === idx
                        ? 'border-cyan-400 bg-white/10'
                        : 'border-cyan-400/20 bg-white/5 hover:bg-white/8'
                    }`}
                    data-testid={`planet-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: planet.color }}
                        />
                        <span className="font-semibold text-sm">
                          {planet.custom_name || `Planet ${idx + 1}`}
                        </span>
                        {planet.custom_name && (
                          <span className="text-xs text-cyan-400/60">✓</span>
                        )}
                      </div>
                      {planet.moons && planet.moons.length > 0 && (
                        <span className="text-xs text-muted-foreground">{planet.moons.length} moon{planet.moons.length > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{planet.type}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Planet Details & Scan */}
            {selectedPlanet && (
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">
                    {selectedPlanet.custom_name || `Planet ${selectedPlanet.idx + 1}`}
                  </h3>
                  {selectedPlanet.custom_name && selectedPlanet.named_by && (
                    <span className="text-xs text-muted-foreground">Named by {selectedPlanet.named_by}</span>
                  )}
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  <p>Type: <span className="text-cyan-400">{selectedPlanet.type}</span></p>
                  <p>Orbit: <span className="text-cyan-400">{selectedPlanet.orbit_au} AU</span></p>
                  <p>Radius: <span className="text-cyan-400">{(selectedPlanet.radius_km / 1000).toFixed(0)}k km</span></p>
                  <p>Atmosphere: <span className="text-cyan-400">{selectedPlanet.atmosphere}</span></p>
                  <p>Habitability: <span className="text-cyan-400">{selectedPlanet.habitability}/100</span></p>
                  {selectedPlanet.moons && selectedPlanet.moons.length > 0 && (
                    <p>Moons: <span className="text-cyan-400">{selectedPlanet.moons.length}</span></p>
                  )}
                </div>

                {!hasScanResult && (
                  <button
                    onClick={handleScanPlanet}
                    disabled={scanning || user.credits < 25}
                    className="w-full py-3 bg-cyan-500/90 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-lg btn-glow transition-all"
                    data-testid="scan-button"
                  >
                    {scanning ? 'Scanning...' : user.credits < 25 ? 'Insufficient Credits' : 'Scan Planet (25 Credits)'}
                  </button>
                )}

                {/* Enter System (3D Gameplay) */}
                <button
                  onClick={() => navigate(`/play/${coords}/planet/${selectedPlanet.idx}`, { state: { from: `/system/${coords}` } })}
                  className="mt-3 w-full py-3 bg-gradient-to-r from-cyan-500/90 to-teal-500/90 hover:from-cyan-400 hover:to-teal-400 text-slate-900 font-semibold rounded-lg btn-glow transition-all flex items-center justify-center gap-2"
                  data-testid="enter-system-planet-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  Enter System
                </button>

                {/* Planet Naming UI - only available after scanning */}
                {hasScanResult && (
                  <div className="mb-4 p-4 bg-white/5 rounded-lg border border-cyan-400/20">
                    <p className="text-xs text-cyan-400 mb-2">
                      {selectedPlanet.custom_name 
                        ? (selectedPlanet.named_by_me ? 'Update Planet Name' : 'Planet Named')
                        : 'Name This Planet'}
                    </p>
                    {(!selectedPlanet.custom_name || selectedPlanet.named_by_me) ? (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={planetName}
                          onChange={(e) => setPlanetName(e.target.value)}
                          placeholder="Enter planet name..."
                          maxLength={50}
                          className="flex-1 px-3 py-2 bg-white/5 border border-cyan-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                          data-testid="planet-name-input"
                        />
                        <button
                          onClick={handleNamePlanet}
                          disabled={naming || !planetName.trim()}
                          className="px-4 py-2 bg-cyan-500/90 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-lg transition-all text-sm"
                          data-testid="name-planet-button"
                        >
                          {naming ? '...' : 'Name'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        This planet was named "{selectedPlanet.custom_name}" by {selectedPlanet.named_by}
                      </p>
                    )}
                  </div>
                )}

                {/* Phase 5 — Claim Home Base (scanned bodies only) */}
                {hasScanResult && (
                  <div className="mb-4 p-4 bg-gradient-to-br from-cyan-950/40 to-transparent rounded-lg border border-cyan-400/20" data-testid="claim-base-section">
                    <p className="text-xs text-cyan-400 mb-2 flex items-center gap-1.5">
                      <span>Home Base</span>
                    </p>
                    {hasBase ? (
                      <p className="text-sm text-muted-foreground">
                        You already own a home base. Manage it from the <button onClick={() => navigate('/base')} className="text-cyan-300 underline">Base</button> page.
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground mb-3">
                          Establish your outpost here — it auto-mines this world's ore around the clock. Cost: 50,000 cr.
                        </p>
                        <button
                          onClick={() => claimBase(null)}
                          disabled={claiming}
                          className="px-4 py-2 bg-cyan-500/90 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-lg transition-all text-sm"
                          data-testid="claim-base-planet-button"
                        >
                          {claiming ? 'Claiming...' : 'Claim Home Base'}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Moons List */}
                {selectedPlanet.moons && selectedPlanet.moons.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-cyan-400 mb-2">Moons ({selectedPlanet.moons.length})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {selectedPlanet.moons.map((moon, moonIdx) => {
                        const moonHasScan = moon.scans && moon.scans.find(s => s.user_email === user.email);
                        return (
                          <button
                            key={moonIdx}
                            onClick={() => handleMoonClick(moon, moonIdx)}
                            className={`w-full text-left p-2 rounded-lg border transition-all text-sm ${
                              selectedMoon?.moon_idx === moonIdx
                                ? 'border-purple-400 bg-purple-500/10'
                                : 'border-white/10 bg-white/5 hover:bg-white/8'
                            }`}
                            data-testid={`moon-${moonIdx}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 rounded-full bg-purple-400" />
                                <span>{moon.custom_name || moon.name}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                {moonHasScan && (
                                  <span className="text-xs text-green-400">Scanned</span>
                                )}
                                <span className="text-xs text-muted-foreground">{(moon.radius_km / 1000).toFixed(1)}k km</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {scanResult && (
                  <div className="mt-4 space-y-4" data-testid="scan-result">
                    {/* Phase 4 — Legendary world reveal */}
                    <LegendaryReveal legendary={scanResult.legendary} />
                    {/* Narrative */}
                    <div className="p-4 bg-white/5 rounded-lg border border-cyan-400/20">
                      <p className="text-xs text-cyan-400 mb-2">Scan Analysis</p>
                      <p className="text-sm leading-relaxed">{scanResult.narrative}</p>
                    </div>

                    {/* Total Estimated Value */}
                    {scanResult.total_estimated_value !== undefined && (
                      <div className="p-4 bg-yellow-400/10 rounded-lg border border-yellow-400/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-yellow-400 mb-1">Total Estimated Value</p>
                            <p className="text-2xl font-bold text-yellow-400">
                              {scanResult.total_estimated_value.toLocaleString()} Credits
                            </p>
                          </div>
                          <svg className="w-10 h-10 text-yellow-400/50" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Elements Found with Values */}
                    {scanResult.elements_found && scanResult.elements_found.length > 0 && (
                      <div className="p-4 bg-white/5 rounded-lg border border-cyan-400/20">
                        <p className="text-xs text-cyan-400 mb-3">Elements Detected (Click for details)</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {scanResult.elements_found.map((element, idx) => {
                            // Handle both old format (string) and new format (object)
                            const elementName = typeof element === 'string' ? element : element.name;
                            const quantity = typeof element === 'object' ? element.quantity_kg : null;
                            const valuePerKg = typeof element === 'object' ? element.value_per_kg : null;
                            const totalValue = typeof element === 'object' ? element.total_value : null;
                            const rarity = typeof element === 'object' ? element.rarity : null;
                            
                            const elementData = allElements.find(el => 
                              el.name.toLowerCase() === elementName.toLowerCase() ||
                              elementName.toLowerCase().includes(el.name.toLowerCase()) ||
                              el.symbol.toLowerCase() === elementName.toLowerCase()
                            );
                            
                            return (
                              <button
                                key={idx}
                                onClick={() => handleElementClick(elementName)}
                                className="w-full flex items-center justify-between hover:bg-white/10 p-2 rounded transition-all text-left group"
                                data-testid={`element-${idx}`}
                              >
                                <div className="flex items-center space-x-2">
                                  <div 
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: getRarityColor(rarity) || '#22D3EE' }}
                                  ></div>
                                  <span className="text-sm group-hover:text-cyan-400 transition-colors">{elementName}</span>
                                  {elementData && elementData.number > 118 && (
                                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full flex-shrink-0" title="Newly discovered element"></div>
                                  )}
                                  {rarity && (
                                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ 
                                      backgroundColor: getRarityColor(rarity) + '20',
                                      color: getRarityColor(rarity)
                                    }}>
                                      {rarity}
                                    </span>
                                  )}
                                </div>
                                {quantity !== null && (
                                  <div className="text-right">
                                    <div className="text-xs text-muted-foreground">
                                      {quantity >= 1000 ? `${(quantity/1000).toFixed(1)}t` : `${quantity.toFixed(0)}kg`} @ {valuePerKg} cr/kg
                                    </div>
                                    <div className="text-sm font-semibold text-yellow-400">
                                      {totalValue >= 1000 ? `${(totalValue/1000).toFixed(1)}k` : totalValue?.toFixed(0)} cr
                                    </div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Atmosphere Composition Chart */}
                    {scanResult.atmosphere_composition && scanResult.atmosphere_composition.length > 0 && (
                      <div className="p-4 bg-white/5 rounded-lg border border-cyan-400/20">
                        <p className="text-xs text-cyan-400 mb-3">Atmosphere Composition</p>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={scanResult.atmosphere_composition}
                              dataKey="percentage"
                              nameKey="gas"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={(entry) => `${entry.gas}: ${entry.percentage}%`}
                              labelLine={false}
                            >
                              {scanResult.atmosphere_composition.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'][index % 6]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value}%`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Mineral Composition Chart */}
                    {scanResult.mineral_composition && scanResult.mineral_composition.length > 0 && (
                      <div className="p-4 bg-white/5 rounded-lg border border-cyan-400/20">
                        <p className="text-xs text-cyan-400 mb-3">Mineral Composition</p>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={scanResult.mineral_composition}
                              dataKey="percentage"
                              nameKey="mineral"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={(entry) => `${entry.mineral}: ${entry.percentage}%`}
                              labelLine={false}
                            >
                              {scanResult.mineral_composition.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#FCD34D', '#FB923C', '#94A3B8', '#6B7280', '#475569', '#1E293B'][index % 6]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value}%`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Flora Composition Chart */}
                    {scanResult.flora_composition && scanResult.flora_composition.length > 0 && (
                      <div className="p-4 bg-white/5 rounded-lg border border-cyan-400/20">
                        <p className="text-xs text-cyan-400 mb-3">Flora Distribution</p>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={scanResult.flora_composition}
                              dataKey="percentage"
                              nameKey="type"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={(entry) => `${entry.type}: ${entry.percentage}%`}
                              labelLine={false}
                            >
                              {scanResult.flora_composition.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#10B981', '#34D399', '#6EE7B7', '#84CC16', '#A3E635', '#BEF264'][index % 6]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value}%`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - 2D Solar System View */}
          <div className="glass-card rounded-xl overflow-hidden" style={{ minHeight: '600px' }} data-testid="solar-system-view">
            <SolarSystemScene2D
              system={system}
              selectedPlanet={selectedPlanet}
              onPlanetClick={handlePlanetClick}
            />
          </div>
        </div>
      </div>

      {/* Element Details Modal */}
      {selectedElement && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedElement(null)}>
          <div className="glass-card rounded-xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="element-modal">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="text-sm text-muted-foreground mb-2">Element {selectedElement.number}</div>
                <h3 className="text-4xl font-bold mb-2" style={{ color: getCategoryColor(selectedElement.category) }}>
                  {selectedElement.symbol} - {selectedElement.name}
                </h3>
              </div>
              <button
                onClick={() => setSelectedElement(null)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="close-element-modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Atomic Mass</div>
                  <div className="text-xl font-semibold">{selectedElement.mass.toFixed(3)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Category</div>
                  <div className="text-xl font-semibold capitalize" style={{ color: getCategoryColor(selectedElement.category) }}>
                    {selectedElement.category.replace(/-/g, ' ')}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground">Discovered</div>
                <div className="text-lg">{selectedElement.discovered}</div>
              </div>

              {selectedElement.number > 118 && (
                <>
                  <div>
                    <div className="text-sm text-muted-foreground">Rarity</div>
                    <div className="text-lg font-semibold" style={{ color: getRarityColor(selectedElement.rarity) }}>
                      {selectedElement.rarity}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Properties</div>
                    <div className="text-sm bg-white/5 rounded-lg p-3 border border-cyan-400/20">
                      {selectedElement.properties}
                    </div>
                  </div>

                  <div className="bg-yellow-400/10 rounded-lg p-3 border border-yellow-400/20">
                    <div className="text-sm text-yellow-400">Newly Discovered Element</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      This element was recently discovered during universe exploration missions.
                    </div>
                  </div>
                </>
              )}

              {selectedElement.number <= 118 && (
                <div className="bg-white/5 rounded-lg p-3 border border-cyan-400/20">
                  <div className="text-sm text-cyan-400">Known Element</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    This is one of the 118 elements currently known to science on Earth.
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setSelectedElement(null)}
              className="mt-6 w-full py-3 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Moon Details Modal */}
      {selectedMoon && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMoon(null)}>
          <div className="glass-card rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="moon-modal">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="text-sm text-purple-400 mb-2">Moon of {selectedPlanet?.custom_name || `Planet ${selectedPlanet?.idx + 1}`}</div>
                <h3 className="text-3xl font-bold mb-2">
                  {selectedMoon.custom_name || selectedMoon.name}
                </h3>
                {selectedMoon.custom_name && selectedMoon.named_by && (
                  <p className="text-xs text-muted-foreground">Named by {selectedMoon.named_by}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedMoon(null)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="close-moon-modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Moon Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Radius</div>
                  <div className="text-xl font-semibold text-purple-400">{(selectedMoon.radius_km / 1000).toFixed(1)}k km</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Orbital Distance</div>
                  <div className="text-xl font-semibold text-purple-400">{(selectedMoon.orbit_km / 1000).toFixed(0)}k km</div>
                </div>
              </div>

              {/* Scan Button */}
              {!moonScanResult && (
                <button
                  onClick={handleScanMoon}
                  disabled={scanningMoon || user.credits < 25}
                  className="w-full py-3 bg-purple-500/90 hover:bg-purple-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all"
                  data-testid="scan-moon-button"
                >
                  {scanningMoon ? 'Scanning...' : user.credits < 25 ? 'Insufficient Credits' : 'Scan Moon (25 Credits)'}
                </button>
              )}

              {/* Enter System (3D Gameplay) */}
              <button
                onClick={() => navigate(`/play/${coords}/planet/${selectedPlanet.idx}/moon/${selectedMoon.moon_idx}`, { state: { from: `/system/${coords}` } })}
                className="w-full py-3 bg-gradient-to-r from-purple-500/90 to-cyan-500/90 hover:from-purple-400 hover:to-cyan-400 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                data-testid="enter-system-moon-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Enter System
              </button>

              {/* Moon Naming UI - only available after scanning */}
              {moonScanResult && (
                <div className="p-4 bg-white/5 rounded-lg border border-purple-400/20">
                  <p className="text-xs text-purple-400 mb-2">
                    {selectedMoon.custom_name 
                      ? (selectedMoon.named_by_me ? 'Update Moon Name' : 'Moon Named')
                      : 'Name This Moon'}
                  </p>
                  {(!selectedMoon.custom_name || selectedMoon.named_by_me) ? (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={moonName}
                        onChange={(e) => setMoonName(e.target.value)}
                        placeholder="Enter moon name..."
                        maxLength={50}
                        className="flex-1 px-3 py-2 bg-white/5 border border-purple-400/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                        data-testid="moon-name-input"
                      />
                      <button
                        onClick={handleNameMoon}
                        disabled={namingMoon || !moonName.trim()}
                        className="px-4 py-2 bg-purple-500/90 hover:bg-purple-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all text-sm"
                        data-testid="name-moon-button"
                      >
                        {namingMoon ? '...' : 'Name'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      This moon was named "{selectedMoon.custom_name}" by {selectedMoon.named_by}
                    </p>
                  )}
                </div>
              )}

              {/* Phase 5 — Claim Home Base on a scanned moon */}
              {(moonScanResult || (selectedMoon.scans && selectedMoon.scans.find(s => s.user_email === user.email))) && (
                <div className="p-4 bg-gradient-to-br from-cyan-950/40 to-transparent rounded-lg border border-cyan-400/20" data-testid="claim-base-moon-section">
                  <p className="text-xs text-cyan-400 mb-2">Home Base</p>
                  {hasBase ? (
                    <p className="text-sm text-muted-foreground">
                      You already own a home base. Manage it from the <button onClick={() => navigate('/base')} className="text-cyan-300 underline">Base</button> page.
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mb-3">
                        Establish your outpost on this moon (auto-mines its ore). Cost: 50,000 cr.
                      </p>
                      <button
                        onClick={() => claimBase(selectedMoon.moon_idx)}
                        disabled={claiming}
                        className="px-4 py-2 bg-cyan-500/90 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-slate-900 font-semibold rounded-lg transition-all text-sm"
                        data-testid="claim-base-moon-button"
                      >
                        {claiming ? 'Claiming...' : 'Claim Home Base'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Moon Scan Results */}
              {moonScanResult && (
                <div className="space-y-4" data-testid="moon-scan-result">
                  {/* Phase 4 — Legendary world reveal */}
                  <LegendaryReveal legendary={moonScanResult.legendary} />
                  {/* Narrative */}
                  <div className="p-4 bg-white/5 rounded-lg border border-purple-400/20">
                    <p className="text-xs text-purple-400 mb-2">Scan Analysis</p>
                    <p className="text-sm leading-relaxed">{moonScanResult.narrative}</p>
                  </div>

                  {/* Total Estimated Value */}
                  {moonScanResult.total_estimated_value !== undefined && (
                    <div className="p-4 bg-yellow-400/10 rounded-lg border border-yellow-400/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-yellow-400 mb-1">Total Estimated Value</p>
                          <p className="text-2xl font-bold text-yellow-400">
                            {moonScanResult.total_estimated_value.toLocaleString()} Credits
                          </p>
                        </div>
                        <svg className="w-8 h-8 text-yellow-400/50" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Elements Found with Values */}
                  {moonScanResult.elements_found && moonScanResult.elements_found.length > 0 && (
                    <div className="p-4 bg-white/5 rounded-lg border border-purple-400/20">
                      <p className="text-xs text-purple-400 mb-3">Elements Detected</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {moonScanResult.elements_found.map((element, idx) => {
                          const elementName = typeof element === 'string' ? element : element.name;
                          const quantity = typeof element === 'object' ? element.quantity_kg : null;
                          const valuePerKg = typeof element === 'object' ? element.value_per_kg : null;
                          const totalValue = typeof element === 'object' ? element.total_value : null;
                          const rarity = typeof element === 'object' ? element.rarity : null;
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => handleElementClick(elementName)}
                              className="w-full flex items-center justify-between hover:bg-white/10 p-2 rounded transition-all text-left group"
                            >
                              <div className="flex items-center space-x-2">
                                <div 
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: getRarityColor(rarity) || '#A855F7' }}
                                ></div>
                                <span className="text-sm group-hover:text-purple-400 transition-colors">{elementName}</span>
                                {rarity && (
                                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ 
                                    backgroundColor: getRarityColor(rarity) + '20',
                                    color: getRarityColor(rarity)
                                  }}>
                                    {rarity}
                                  </span>
                                )}
                              </div>
                              {quantity !== null && (
                                <div className="text-right">
                                  <div className="text-xs text-muted-foreground">
                                    {quantity >= 1000 ? `${(quantity/1000).toFixed(1)}t` : `${quantity.toFixed(0)}kg`} @ {valuePerKg} cr/kg
                                  </div>
                                  <div className="text-sm font-semibold text-yellow-400">
                                    {totalValue >= 1000 ? `${(totalValue/1000).toFixed(1)}k` : totalValue?.toFixed(0)} cr
                                  </div>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Mineral Composition Chart */}
                  {moonScanResult.mineral_composition && moonScanResult.mineral_composition.length > 0 && (
                    <div className="p-4 bg-white/5 rounded-lg border border-purple-400/20">
                      <p className="text-xs text-purple-400 mb-3">Mineral Composition</p>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={moonScanResult.mineral_composition}
                            dataKey="percentage"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={60}
                            label={(entry) => `${entry.name}: ${entry.percentage}%`}
                            labelLine={false}
                          >
                            {moonScanResult.mineral_composition.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#A855F7', '#C084FC', '#DDD6FE', '#6B7280', '#475569'][index % 5]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => setSelectedMoon(null)}
              className="mt-6 w-full py-3 bg-purple-500/90 hover:bg-purple-400 text-white font-semibold rounded-lg transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemView;
